import { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from '../hooks/useTranslation';
import ErrorLoading from '../components/ErrorLoading';

const DOCS = [
  { key: 'root', file: 'root.md', labelKey: 'docs.tabRoot' },
  { key: 'backend', file: 'backend.md', labelKey: 'docs.tabBackend' },
  { key: 'frontend', file: 'frontend.md', labelKey: 'docs.tabFrontend' },
];

function Docs() {
  const { t } = useTranslation();
  const [active, setActive] = useState('root');
  const [contents, setContents] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (contents[active] !== undefined) return;
    const doc = DOCS.find((d) => d.key === active);
    if (!doc) return;
    const controller = new AbortController();
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`/docs/${doc.file}`, { signal: controller.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const text = await res.text();
        setContents((prev) => ({ ...prev, [active]: text }));
      } catch (err) {
        if (err.name === 'AbortError') return;
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [active, contents]);

  const markdown = useMemo(() => contents[active] ?? '', [contents, active]);

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>
          <i className="bi bi-book me-2" aria-hidden="true"></i>
          {t('docs.title')}
        </h2>
      </div>

      <ul className="nav nav-tabs mb-3">
        {DOCS.map((d) => (
          <li className="nav-item" key={d.key}>
            <button
              type="button"
              className={`nav-link ${active === d.key ? 'active' : ''}`}
              onClick={() => setActive(d.key)}
            >
              {t(d.labelKey)}
            </button>
          </li>
        ))}
      </ul>

      {error ? (
        <ErrorLoading error={error} />
      ) : loading && !markdown ? (
        <div className="text-muted">{t('common.loading')}</div>
      ) : (
        <article className="markdown-body">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              table: ({ node, ...props }) => (
                <div className="table-responsive">
                  <table className="table table-sm table-bordered" {...props} />
                </div>
              ),
              code: ({ inline, className, children, ...props }) =>
                inline ? (
                  <code className={className} {...props}>
                    {children}
                  </code>
                ) : (
                  <pre className="p-3 bg-body-secondary rounded small">
                    <code className={className} {...props}>
                      {children}
                    </code>
                  </pre>
                ),
              a: ({ node, ...props }) => (
                <a target="_blank" rel="noopener noreferrer" {...props} />
              ),
            }}
          >
            {markdown}
          </ReactMarkdown>
        </article>
      )}
    </div>
  );
}

export default Docs;
